import { Tab, Tabs } from "react-bootstrap";
import { useFormContext, useWatch } from "react-hook-form";
import { Table } from "stories";
import { currencyStyle } from "utils/customHooks/currencyFormat";

const projectOverviewHeader = [
  {
    id: "title",
    title: "Title",
    align: "left",
  },
  {
    id: "value",
    title: "Value",
    align: "right",
  },
];

const generateMortgagePosition = (mp) => {
  return {
    first: "1",
    second: "2",
    "third+": "3+",
  }[mp];
};

export const UnderWritingSummeryTable = () => {
  const form = useFormContext();
  const [
    loanType,
    projectPurpose,
    assetClasses,
    address,
    refinanceLoan,
    netAmountRequested,
    mortgagePosition,
    loanTerms,
    loanStructure,
    amortizationPeriod,
    interestRate,
    premium,
    proposedInterestRate,
    annualDebtService,
    existingDebtOnProperty,
    totalDebtAmount,
    totalCashEquity,
    marketValueOfProperty,
    additionalRequiredCashEquity,
    LTV,
  ] = useWatch({
    name: [
      "loan_type",
      "project_purpose",
      "asset_classes",
      "address",
      "refinance_loan",
      "net_amount_requested",
      "mortgage_position",
      "loan_terms",
      "loan_structure",
      "amortization_period",
      "interest_rate",
      "premium",
      "proposed_interest_rate",
      "annual_debt_service",
      "existing_debt_on_property",
      "total_debt_amount",
      "total_cash_equity",
      "total_asset_value",
      "cash_equity_difference",
      "ltv",
    ],
    control: form.control,
  });
  const projectOverviewRows = [
    {
      title: "Loan Type",
      value: loanType + (refinanceLoan ? " (Refinance)" : ""),
    },
    {
      title: "Project Purpose",
      value: projectPurpose,
      props: {
        value: {
          className: "capitalize",
        },
      },
    },
    {
      title: "Asset Class",
      value: assetClasses ? assetClasses.join(", ") : "",
      props: {
        value: {
          className: "capitalize",
        },
      },
    },
    {
      title: "Location",
      value: [
        address.address,
        address.city,
        address.state_or_province + ` (${address.postal_code})`,
      ].join(", "),
    },
  ];

  let loanRequestRows = [
    {
      title: "Requested Loan Amount",
      value: `$ ${currencyStyle(netAmountRequested)}`,
    },
    {
      title: "Mortgage Position",
      value: generateMortgagePosition(mortgagePosition),
    },
    {
      title: `Loan Term` + (loanTerms ? ` (${loanTerms.unit}s)` : ""),
      value: loanTerms ? loanTerms.count : "",
    },
    {
      title: "Loan Structure",
      value: loanStructure,
      props: {
        value: {
          className: "capitalize",
        },
      },
    },
  ];
  if (loanStructure !== "interest only") {
    loanRequestRows.push({
      title:
        `Amortization Period` +
        (amortizationPeriod ? ` (${amortizationPeriod.unit}s)` : ""),
      value: amortizationPeriod ? amortizationPeriod.count : "",
    });
  }
  let interestRateView = interestRate ? interestRate : undefined;
  if (interestRateView && premium && proposedInterestRate === "variable")
    interestRateView += premium;
  loanRequestRows = [
    ...loanRequestRows,
    {
      title: "Interest Rate",
      value: `${interestRateView ? interestRateView + "%" : ""} ${
        proposedInterestRate ? proposedInterestRate : ""
      }`,
      props: {
        value: {
          className: "uppercase",
        },
      },
    },
    {
      title: "Annual Debt Service",
      value: annualDebtService
        ? `$ ${currencyStyle(annualDebtService)}`
        : "$ -",
    },
  ];

  const financialAnalysisRows = [
    {
      title: "Existing Debt on Property",
      value: `$ ${currencyStyle(existingDebtOnProperty)}`,
    },
    {
      title: "Total Debt Amount",
      value: `$ ${currencyStyle(totalDebtAmount)}`,
    },
    {
      title: "Total Cash Equity",
      value: `$ ${currencyStyle(totalCashEquity)}`,
    },
    {
      title: "Market Value of Property",
      value: `$ ${currencyStyle(marketValueOfProperty.total_price)}`,
    },
    {
      title: "LTV",
      value: typeof LTV !== "undefined" || LTV !== null ? LTV + "%" : "",
      props: {
        title: {},
        value: {},
      },
    },
    {
      title:
        additionalRequiredCashEquity < 0
          ? "Eligible Equity Takeout"
          : "Additional Required Cash Equity",
      value: `$ ${currencyStyle(
        Number(Math.abs(additionalRequiredCashEquity).toFixed(2))
      )}`,
      props: {
        title: {},
        value: {},
      },
    },
  ];
  return (
    <Tabs activeKey={"underwriting"} className="mt-4">
      <Tab
        eventKey={"underwriting"}
        title="Underwriting Summary"
        tabClassName="capitalize text-left"
      >
        <div className="mb-2">
          <span className="fw-bold title mb-2 d-block">Project Overview</span>
          <Table
            sameColumnWidth
            hideHeader
            horizontal
            headers={projectOverviewHeader}
            rows={projectOverviewRows}
          />
        </div>
        <div className="mb-2">
          <span className="fw-bold title mb-2 d-block">Loan Request</span>
          <Table
            sameColumnWidth
            hideHeader
            horizontal
            headers={projectOverviewHeader}
            rows={loanRequestRows}
          />
        </div>

        <div className="mb-2">
          <span className="fw-bold title mb-2 d-block">Financial Analysis</span>
          <Table
            sameColumnWidth
            hideHeader
            horizontal
            headers={projectOverviewHeader}
            rows={financialAnalysisRows}
          />
        </div>
      </Tab>
    </Tabs>
  );
};
